import moment from 'moment-timezone';

/**
 * Interface for event data
 */
interface EventData {
  start_time: string;
  end_time: string;
  subject: string;
  [key: string]: any;
}

/**
 * Interface for user availability data
 */
interface AvailabilityData {
  start: string;
  end: string;
}

/**
 * Interface for interviewer data
 */
interface InterviewerData {
  busyEvents?: Array<EventData & { userInfo?: any }>;
  availability?: AvailabilityData[];
  [key: string]: any;
}

/**
 * Interface for week data
 */
interface WeekData {
  [interviewerId: string]: InterviewerData;
}

/**
 * Interface for color code mapping
 */
type ColorCodes = Record<string, ColorVariant>;

type ColorVariant = { light: string; dark: string };

/**
 * Interface for color definitions
 */
interface ColorDefinition {
  light: string;
  dark: string;
}

/**
 * Interface for color mappings
 */
interface Colors {
  [colorCode: string]: ColorDefinition;
}

/**
 * Options for the processCalendarEvents function
 */
interface ProcessEventsOptions {
  dataForCurrentWeek: WeekData;
  colorCodes: ColorCodes;
  COLORS: Colors;
  timezone: string;
  dateFormat: { label: string };
}

/**
 * Result of processCalendarEvents function
 */
interface ProcessEventsResult {
  allEvents: any[];
  nonAvailabilityEvents: any[];
  hasAllDayEvent: boolean;
}

/**
 * Determines if an event spans the entire day (or multiple days)
 * @param {string} startTime - ISO string representing event start time
 * @param {string} endTime - ISO string representing event end time
 * @returns {boolean} True if event is a full day event
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isFullDayEvent = (startTime: string, endTime: string): boolean => {
  const start = moment(startTime);
  const end = moment(endTime);

  return (
    start.hours() === 0 &&
    start.minutes() === 0 &&
    start.seconds() === 0 &&
    end.diff(start, 'hours') >= 24
  );
};

interface Event {
  subject: string;
  start_time: string;
  end_time: string;
}

interface DayEvent {
  subject: string;
  date: string;
  isFullDay: boolean;
  start_time?: string;
  end_time?: string;
  eventType?: string;
}

/**
 * Helper function to process raw busy events, potentially splitting multi-day events.
 */
const processFullDayEvents = (
  events: Event[],
  timezone: string,
  dateFormat: { label: string }
): DayEvent[] => {
  const dayEvents: DayEvent[] = [];
  let eventCounter = 0;

  if (!Array.isArray(events)) {
    console.warn('Input events is not an array.');
    return dayEvents;
  }
  // Validate dateFormat early
  if (!dateFormat || !dateFormat.label) {
    console.error('Invalid dateFormat provided:', dateFormat);
    return dayEvents; // Cannot proceed without date format
  }

  events.forEach((event, index) => {
    eventCounter++;

    // Basic validation of input event structure
    if (!event.start_time || !event.end_time) {
      console.warn(
        `Event ${eventCounter} skipped - missing start_time or end_time.`
      );
      return; // Skip this event if essential data is missing
    }

    // Parse dates using the timezone info in the timestamp AND target timezone
    const startMoment = moment.tz(event.start_time, timezone);
    const endMoment = moment.tz(event.end_time, timezone);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      console.warn(
        `Event ${eventCounter} skipped - invalid date parsing. Start valid: ${startMoment.isValid()}, End valid: ${endMoment.isValid()}`
      );
      return; // Skip invalid dates
    }

    const timeZoneOffset = startMoment.format('Z'); // Preserve original offset for full day construction

    // Check midnight boundaries
    const isStartMidnight =
      startMoment.hours() === 0 &&
      startMoment.minutes() === 0 &&
      startMoment.seconds() === 0;
    const isEndMidnight =
      endMoment.hours() === 0 &&
      endMoment.minutes() === 0 &&
      endMoment.seconds() === 0;

    // Adjust end day for comparison if event ends exactly at midnight
    let endDayAdjusted = isEndMidnight
      ? moment(endMoment).subtract(1, 'day')
      : endMoment;

    // Get start/end dates based on target timezone
    const startDate = startMoment.format(dateFormat?.label);
    const endDate = endDayAdjusted.format(dateFormat?.label);

    // Calculate calendar days spanned
    const startClone = moment(startMoment).startOf('day');
    const endClone = moment(endDayAdjusted).startOf('day');
    const calendarDays = endClone.diff(startClone, 'days') + 1;

    // --- Logic for processing based on days spanned ---

    if (isStartMidnight && isEndMidnight && startMoment.isBefore(endMoment)) {
      // Perfect Full Day(s)
      const fullDays = moment(endMoment).diff(startMoment, 'days');

      for (let i = 0; i < fullDays; i++) {
        const dayMoment = moment(startClone).add(i, 'days');
        const dayDate = dayMoment.format(dateFormat.label);
        const isoStart =
          dayMoment.format('YYYY-MM-DD') + `T00:00:00${timeZoneOffset}`;
        const isoEnd =
          dayMoment.format('YYYY-MM-DD') + `T23:59:59${timeZoneOffset}`;
        const eventToAdd: DayEvent = {
          ...event,
          subject: event.subject,
          date: dayDate,
          isFullDay: true,
          start_time: isoStart,
          end_time: isoEnd,
        };

        dayEvents.push(eventToAdd);
      }
    } else if (calendarDays === 1) {
      // Single Calendar Day

      const durationHours = endMoment.diff(startMoment, 'hours', true);
      const isFullDay = isStartMidnight && durationHours >= 23.99; // Covers 00:00 to ~23:59
      const eventToAdd: DayEvent = {
        ...event,
        subject: event.subject,
        date: startDate,
        isFullDay,
        // Output as UTC ISO string for consistency downstream
        start_time: startMoment.toISOString(),
        end_time: endMoment.toISOString(),
      };

      dayEvents.push(eventToAdd);
    } else {
      // Multi-Calendar Day

      // First day
      if (!isStartMidnight) {
        const firstDayEnd = startMoment.clone().endOf('day');
        const eventToAdd: DayEvent = {
          ...event,
          subject: event.subject,
          date: startDate,
          isFullDay: false,
          start_time: startMoment.toISOString(), // UTC
          end_time: firstDayEnd.toISOString(), // UTC
        };

        dayEvents.push(eventToAdd);
      } else {
        const isoStart =
          startMoment.format('YYYY-MM-DD') + `T00:00:00${timeZoneOffset}`;
        const isoEnd =
          startMoment.format('YYYY-MM-DD') + `T23:59:59${timeZoneOffset}`;
        const eventToAdd: DayEvent = {
          ...event,
          subject: event.subject,
          date: startDate,
          isFullDay: true,
          start_time: isoStart,
          end_time: isoEnd,
        };

        dayEvents.push(eventToAdd);
      }

      // Middle days (if any)
      const middleDaysCount = calendarDays - 2; // Subtract first and last potential partial days

      for (let i = 1; i <= middleDaysCount; i++) {
        // Loop from second day up to second-to-last day
        const dayMoment = moment(startClone).add(i, 'days');
        const dayDate = dayMoment.format(dateFormat.label);
        const isoStart =
          dayMoment.format('YYYY-MM-DD') + `T00:00:00${timeZoneOffset}`;
        const isoEnd =
          dayMoment.format('YYYY-MM-DD') + `T23:59:59${timeZoneOffset}`;
        const eventToAdd: DayEvent = {
          ...event,
          subject: event.subject,
          date: dayDate,
          isFullDay: true,
          start_time: isoStart,
          end_time: isoEnd,
        };

        dayEvents.push(eventToAdd);
      }

      // Last day
      if (!isEndMidnight) {
        // Only add last partial day if it didn't end exactly at midnight
        const lastDayStart = endDayAdjusted.clone().startOf('day');
        const eventToAdd: DayEvent = {
          ...event,
          subject: event.subject,
          date: endDate,
          isFullDay: false,
          start_time: lastDayStart.toISOString(), // UTC
          end_time: endMoment.toISOString(), // UTC
        };

        dayEvents.push(eventToAdd);
      }
    }
  });

  return dayEvents;
};

/**
 * Main function to process events data for a week.
 */
export const schProcessCalendarEvents = ({
  dataForCurrentWeek,
  colorCodes,
  COLORS,
  timezone,
  dateFormat,
}: ProcessEventsOptions): ProcessEventsResult => {
  const allEvents: any[] = [];
  const nonAvailabilityEvents: any[] = [];
  let hasAllDayEvent = false;

  // Validate dateFormat
  if (!dateFormat || !dateFormat.label) {
    console.error('Invalid dateFormat received:', dateFormat);
    return { allEvents, nonAvailabilityEvents, hasAllDayEvent };
  }

  if (!dataForCurrentWeek || Object.keys(dataForCurrentWeek).length === 0) {
    console.warn('No data provided for the current week.');
    return { allEvents, nonAvailabilityEvents, hasAllDayEvent };
  }

  Object.entries(dataForCurrentWeek).forEach(
    ([interviewerId, interviewerData]) => {
      const userEvents = interviewerData?.busyEvents || [];
      const userAvailability = interviewerData?.availability || [];
      let { light, dark } = colorCodes?.[interviewerId] || COLORS?.['pastel-1'];

      // --- Process Busy Events ---
      let processedEvents: DayEvent[] = [];
      if (userEvents.length > 0) {
        try {
          processedEvents = processFullDayEvents(
            userEvents,
            timezone,
            dateFormat
          );
        } catch (error) {
          console.error(
            `Interviewer ${interviewerId} - Error during processFullDayEvents:`,
            error
          );
          processedEvents = []; // Reset on error
        }
      } else {
      }

      if (Array.isArray(processedEvents) && processedEvents.length > 0) {
        processedEvents.forEach((eventData, index) => {
          // @ts-ignore
          const { userInfo, ...event } = eventData;

          if (event.eventType === 'meeting_room') {
            light = 'rgb(211, 211, 233)';
            dark = 'rgb(147, 145, 162)';
          }

          // Check for full day event
          const isAll = event.isFullDay;
          if (isAll) hasAllDayEvent = true;

          const baseEventObject = {
            ...event, // Includes subject, date, start_time, end_time etc.
            start: event.start_time, // Use the processed start/end times
            end: event.end_time,
            title: event.subject,
            interviewerId, // Ensure interviewerId is attached
            backgroundColor: light,
            textColor: dark,
            borderColor: light,
            extendedProps: {
              priority: 0,
              type: 'busy',
              dark,
              light,
              userInfo,
              interviewerId, // Repeat interviewerId in extendedProps if needed downstream
            },
          };

          // Add the specific allDay: true version if it's a full day event
          if (isAll) {
            allEvents.push({
              ...baseEventObject,
              allDay: true,
            });
          }

          allEvents.push({
            ...baseEventObject,
            allDay: false, // Ensure this is set for the time grid
          });
        });
      } else {
      }

      // --- Process Non-Availability ---
      if (Array.isArray(userAvailability)) {
        userAvailability.forEach(({ start, end }) => {
          nonAvailabilityEvents.push({
            id: `${interviewerId}-${start}-${end}`, // Unique ID for non-availability
            start,
            end,
            groupId: `interviewer-inverse-background`, // Consistent group ID
            display: 'inverse-background',
          });
        });
      }
    }
  );

  return { allEvents, nonAvailabilityEvents, hasAllDayEvent };
};
